import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { usd } from "@/lib/format";
import { Shield } from "@/components/Logo";
import { useBranding } from "@/context/BrandingContext";
import { Check, ShieldCheck, Layers, PenLine, CreditCard, Loader2, Home, FileDown } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const assetSrc = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
import { toast } from "sonner";
import { ROOF_TYPE_TONE, tLabel } from "@/lib/roofTypes";
import { ProposalSystemsView, InvestmentSummary } from "@/components/ProposalSystemsView";

const SYSTEM = [
  ["Roof Deck", "Inspected & re-nailed to code", "https://static.prod-images.emergentagent.com/jobs/b4624a9d-c450-44a4-a99e-4a1678bc709b/images/b6d3a28a2759581110d7d928b42844fd8748bf0c8c0e2ab17216b4b2de04c419.jpeg"],
  ["Underlayment", "Synthetic moisture barrier, fully sealed", "https://static.prod-images.emergentagent.com/jobs/b4624a9d-c450-44a4-a99e-4a1678bc709b/images/1a8945f4dbc44e2f41d2b3d12c4a968fbf5eb1281719003ff2a97b4fff68b064.jpeg"],
  ["Shingles / Panels", "Manufacturer-certified installation", "https://static.prod-images.emergentagent.com/jobs/b4624a9d-c450-44a4-a99e-4a1678bc709b/images/825a625945cae5694fee8fad1a37fcafe49c1f4cc81794b59927da08c08f7735.jpeg"],
  ["Flashing", "New step & valley flashing at all penetrations", "https://static.prod-images.emergentagent.com/jobs/b4624a9d-c450-44a4-a99e-4a1678bc709b/images/454058de7c51c3c5ac9edc2f5c5e275905d2b189e30ed6cadfb68c6f824a4b37.jpeg"],
  ["Ventilation", "Balanced ridge & intake ventilation", "https://static.prod-images.emergentagent.com/jobs/b4624a9d-c450-44a4-a99e-4a1678bc709b/images/57e610f9e8313503e3e0cde94b4377d53e0932cbbf834cadc6a5edfd1118f926.jpeg"],
];

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const draw = (e, start) => {
    const c = ref.current, r = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    const ctx = c.getContext("2d");
    if (start) { ctx.beginPath(); ctx.moveTo(x, y); drawing.current = true; }
    else if (drawing.current) { ctx.lineTo(x, y); ctx.strokeStyle = "#0B2545"; ctx.lineWidth = 2.5; ctx.stroke(); onChange(c.toDataURL()); }
  };
  return (
    <canvas ref={ref} width={500} height={140} data-testid="signature-pad"
      className="w-full border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 touch-none"
      onMouseDown={(e) => draw(e, true)} onMouseMove={(e) => draw(e)} onMouseUp={() => (drawing.current = false)}
      onTouchStart={(e) => draw(e, true)} onTouchMove={(e) => draw(e)} onTouchEnd={() => (drawing.current = false)} />
  );
}

export default function PublicProposal() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [tier, setTier] = useState("SHINGLE");
  const [signer, setSigner] = useState("");
  const [sig, setSig] = useState(null);
  const [signing, setSigning] = useState(false);
  const [paying, setPaying] = useState(false);
  const { branding } = useBranding();

  const load = () => api.get(`/proposals/public/${id}`).then((r) => { setP(r.data); setTier(r.data.selected_tier); }).catch(() => {});
  useEffect(() => { load(); }, [id]);
  if (!p) return <div className="min-h-screen flex items-center justify-center bg-cloud text-navy font-display text-xl">Loading proposal…</div>;

  const tiers = p.tiers || [];
  const selectedTier = tiers.find((t) => t.tier === tier) || tiers[0] || { price_cents: p.amount_cents };
  const signed = p.status === "signed";
  const multi = (p.systems || []).length > 0;

  const selectTier = async (t) => { setTier(t); await api.post(`/proposals/public/${id}/select`, { tier: t }); load(); };
  const picked = p.selected_upgrade_ids || [];
  const togglePick = async (uid) => {
    if (signed || p.deposit_paid) return;
    const next = picked.includes(uid) ? picked.filter((x) => x !== uid) : [...picked, uid];
    setP((cur) => ({ ...cur, selected_upgrade_ids: next }));
    try { const { data } = await api.post(`/proposals/public/${id}/upgrades`, { upgrade_ids: next }); setP((cur) => ({ ...cur, ...data })); }
    catch { toast.error("Could not update upgrades"); load(); }
  };

  const sign = async () => {
    if (!signer.trim()) return toast.error("Type your full legal name");
    setSigning(true);
    try { await api.post(`/proposals/public/${id}/sign`, { signed_by: signer, signature_data: sig, origin_url: window.location.origin }); toast.success("Contract signed!"); await load(); }
    catch { toast.error("Signing failed"); } finally { setSigning(false); }
  };

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/deposit", { proposal_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Payment init failed"); setPaying(false); }
  };

  return (
    <div className="min-h-screen bg-cloud">
      <header className="bg-navy text-white px-5 py-4 flex items-center justify-between">
        <Shield size={40} withText />
        <div className="text-right text-xs text-slate-300 hidden sm:block leading-tight">
          {branding.phone && <div data-testid="prop-brand-phone">{branding.phone}</div>}
          {branding.website && <div data-testid="prop-brand-website">{branding.website}</div>}
          <div className="mt-0.5">Prepared for <b className="text-white">{p.contact?.name}</b></div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 sm:p-8 space-y-6">
        {(() => {
          const addr = p.property?.address || p.contact?.address || "";
          const choice = p.cover_choice || "auto";
          let coverUrl = null;
          if (choice === "ai") coverUrl = p.visuals?.after || null;
          else if (choice === "street" && branding.has_google && addr) coverUrl = `${API_BASE}/streetview?address=${encodeURIComponent(addr)}`;
          else if (addr) coverUrl = `${API_BASE}/aerial?address=${encodeURIComponent(addr)}`;
          return coverUrl ? (
            <div className="rounded-2xl overflow-hidden border border-slate-200" data-testid="prop-cover">
              <img src={coverUrl} alt="Your home" className="w-full h-56 sm:h-72 object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
            </div>
          ) : null;
        })()}
        <div className="rounded-2xl p-6 sm:p-10 text-white hero-grain" style={{ background: "linear-gradient(120deg,#0B2545,#0D305C)" }}>
          <div className="inline-flex items-center gap-2 bg-brandred px-3 py-1 rounded-full text-xs font-bold uppercase mb-4">Your New Roof Proposal</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase leading-none">{p.contact?.name}</h1>
          <p className="text-slate-300 mt-2 flex items-center gap-2"><Home className="w-4 h-4" /> {p.property?.address || p.contact?.address}</p>
          <a data-testid="prop-download-pdf" href={`${API_BASE}/proposals/${id}/pdf`} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-full text-sm font-semibold transition-colors"><FileDown className="w-4 h-4" /> Download PDF</a>
        </div>

        {p.selected_product && (
          <div className="mb-card p-5 flex items-center gap-4" data-testid="prop-selection">
            {p.selected_color_hex && <div className="w-14 h-14 rounded-xl border border-slate-200 shrink-0" style={{ backgroundColor: p.selected_color_hex }} />}
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400">Your Roof Selection</div>
              <div className="font-display text-xl font-bold uppercase text-navy">{p.selected_product}</div>
              {p.selected_color && <div className="text-sm text-slate-500">Color: <b className="text-navy">{p.selected_color}</b></div>}
            </div>
          </div>
        )}

        {(p.visuals?.before || p.visuals?.after) && (
          <div className="mb-card p-5" data-testid="prop-visuals">
            <h2 className="font-display text-2xl font-bold uppercase text-navy mb-1">Your Home, Reimagined</h2>
            {(p.visuals?.material || p.visuals?.color) && <p className="text-xs text-slate-500 mb-3">{[p.visuals?.material, p.visuals?.color].filter(Boolean).join(" · ")}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-xs font-semibold uppercase text-slate-400 mb-1">Before</div><img src={p.visuals.before} alt="before" className="rounded-xl w-full h-48 object-cover" /></div>
              <div><div className="text-xs font-semibold uppercase text-brandred mb-1">After</div><img src={p.visuals.after} alt="after" className="rounded-xl w-full h-48 object-cover" /></div>
            </div>
          </div>
        )}

        {(() => {
          const sec = p.sections || {};
          const mfr = (branding.manufacturers || []).find((m) => (m.name || "").toLowerCase() === (p.manufacturer || "").toLowerCase());
          const Doc = ({ d }) => d.type === "pdf"
            ? <a href={assetSrc(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-navy hover:border-navy"><FileDown className="w-4 h-4" /> {d.name || "View document"}</a>
            : <img src={assetSrc(d.url)} alt={d.name || ""} className="w-full rounded-xl border border-slate-200" />;
          return (
            <>
              {p.before_photos?.length > 0 && (
                <div className="mb-card p-5" data-testid="prop-before-photos">
                  <h2 className="font-display text-2xl font-bold uppercase text-navy mb-3">Existing Roof Conditions</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {p.before_photos.map((ph, i) => (<div key={i}><img src={assetSrc(ph.url)} alt="" className="rounded-xl w-full h-48 object-cover" />{ph.caption && <p className="text-xs text-slate-500 mt-1">{ph.caption}</p>}</div>))}
                  </div>
                </div>
              )}
              {p.after_photos?.length > 0 && (
                <div className="mb-card p-5" data-testid="prop-after-photos">
                  <h2 className="font-display text-2xl font-bold uppercase text-navy mb-3">Recent Projects</h2>
                  <div className="grid sm:grid-cols-2 gap-3">{p.after_photos.map((ph, i) => (<img key={i} src={assetSrc(ph.url)} alt="" className="rounded-xl w-full h-48 object-cover" />))}</div>
                </div>
              )}
              {sec.sales_sheet && mfr && mfr.sales_sheets?.length > 0 && (
                <div className="mb-card p-5" data-testid="prop-salessheet"><h2 className="font-display text-2xl font-bold uppercase text-navy mb-3">{mfr.name} Roofing System</h2><div className="space-y-3">{mfr.sales_sheets.map((d, i) => <Doc key={i} d={d} />)}</div></div>
              )}
              {sec.color_catalog && mfr && mfr.color_catalogs?.length > 0 && (
                <div className="mb-card p-5" data-testid="prop-catalog"><h2 className="font-display text-2xl font-bold uppercase text-navy mb-3">{mfr.name} Color Options</h2><div className="space-y-3">{mfr.color_catalogs.map((d, i) => <Doc key={i} d={d} />)}</div></div>
              )}
              {sec.about_us && branding.about_us && (
                <div className="mb-card p-6" data-testid="prop-about"><h2 className="font-display text-2xl font-bold uppercase text-navy mb-2">Who We Are</h2><p className="text-sm text-slate-600 leading-relaxed">{branding.about_us}</p>{branding.about_owner && <div className="mt-4 border-l-4 border-brandred bg-slate-50 rounded-r-xl p-4 text-sm italic text-slate-600">{branding.about_owner}</div>}</div>
              )}
              {sec.testimonials && branding.testimonials?.length > 0 && (
                <div data-testid="prop-testimonials"><h2 className="font-display text-2xl font-bold uppercase text-navy mb-3">What Homeowners Say</h2><div className="grid sm:grid-cols-2 gap-4">{branding.testimonials.slice(0, 6).map((t, i) => (<div key={i} className="mb-card p-4"><p className="text-sm italic text-slate-600">"{t.text}"</p><div className="text-xs font-bold text-navy mt-2">— {t.name || "Verified Review"}</div></div>))}</div></div>
              )}
              {sec.awards && branding.awards?.length > 0 && (
                <div className="mb-card p-5" data-testid="prop-awards"><h2 className="font-display text-2xl font-bold uppercase text-navy mb-3">Trusted & Certified</h2><div className="flex flex-wrap gap-3">{branding.awards.map((a, i) => (<div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">{a.image_url ? <img src={assetSrc(a.image_url)} alt="" className="w-6 h-6 object-contain" /> : <ShieldCheck className="w-4 h-4 text-brandred" />}<span className="text-xs font-semibold text-navy">{a.label}</span></div>))}</div></div>
              )}
            </>
          );
        })()}

        {multi && <ProposalSystemsView p={p} />}

        {!multi && <>
        {/* Roof type options */}
        <div>
          <h2 className="font-display text-2xl font-bold uppercase text-navy mb-3">Choose Your System</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {tiers.map((t) => (
              <button key={t.tier} data-testid={`tier-${t.tier}`} disabled={signed} onClick={() => selectTier(t.tier)}
                className={`text-left rounded-2xl p-5 border-2 transition-all ${tier === t.tier ? "border-navy shadow-lg -translate-y-1 bg-white" : "border-slate-200 bg-white"}`}>
                <div className={`inline-block ${ROOF_TYPE_TONE[t.tier]} text-white text-xs font-bold uppercase px-3 py-1 rounded-full mb-3`}>{tLabel(t.tier)}</div>
                <div className="text-sm text-slate-500">{t.product}</div>
                <div className="font-num text-3xl font-extrabold text-navy mt-1">{usd(t.price_cents)}</div>
                {t.inclusions?.length > 0 && (
                  <ul className="mt-3 space-y-1" data-testid={`tier-incl-${t.tier}`}>
                    {t.inclusions.map((x, k) => <li key={k} className="flex items-start gap-1.5 text-xs text-slate-600"><Check className="w-3.5 h-3.5 text-successg shrink-0 mt-0.5" /> {x}</li>)}
                  </ul>
                )}
                {tier === t.tier && <div className="mt-3 flex items-center gap-1 text-successg text-sm font-bold"><Check className="w-4 h-4" /> Selected</div>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div className="mb-card p-5">
            <h3 className="font-display font-bold uppercase text-navy mb-3 flex items-center gap-2"><Layers className="w-5 h-5" /> Scope of Work</h3>
            {(p.scope_items?.length ? p.scope_items.map((i) => [i.title, i.description, i.image_url]) : SYSTEM).map(([a, b, img]) => (<div key={a} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">{img ? <img src={img} alt={a} loading="lazy" className="w-14 h-14 rounded-lg object-cover shrink-0 border border-slate-200" /> : <div className="w-14 h-14 rounded-lg bg-slate-100 shrink-0 border border-slate-200" />}<div className="flex-1"><div className="text-sm font-semibold text-navy flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-successg shrink-0" /> {a}</div><div className="text-xs text-slate-500 mt-0.5">{b}</div></div></div>))}
          </div>
          <div className="mb-card p-5">
            <h3 className="font-display font-bold uppercase text-navy mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Warranty & Financing</h3>
            <p className="text-sm text-slate-600">Lifetime workmanship warranty + manufacturer material warranty. Financing available via Sunlight & GreenSky — 0% intro options, most approved same day.</p>
            <div className="mt-3 space-y-2">
              {p.faqs?.slice(0, 3).map((f) => (<details key={f.id} className="text-sm"><summary className="font-semibold text-navy cursor-pointer">{f.q}</summary><p className="text-slate-500 mt-1">{f.a}</p></details>))}
            </div>
          </div>
        </div>
        </>}

        {p.upgrades?.length > 0 && (
          <div data-testid="prop-upgrades">
            <h2 className="font-display text-2xl font-bold uppercase text-navy mb-1">Optional Upgrades</h2>
            {!signed && <p className="text-sm text-slate-500 mb-3">Tap any upgrade to add it to your total.</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              {p.upgrades.map((u, i) => {
                const on = picked.includes(u.id);
                return (
                <button type="button" key={u.id || i} onClick={() => u.id && togglePick(u.id)} disabled={signed || !u.id} aria-pressed={on}
                  className={`mb-card p-4 flex gap-4 text-left border-2 transition-colors ${on ? "border-successg bg-successg/5" : "border-transparent hover:border-slate-300"} ${signed ? "cursor-default" : ""} ${signed && !on ? "opacity-50" : ""}`} data-testid={`upgrade-${i}`}>
                  <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 mt-1 ${on ? "bg-successg border-successg text-white" : "border-slate-300"}`} data-testid={`upgrade-check-${i}`}>{on && <Check className="w-4 h-4" />}</span>
                  {u.image_url ? <img src={assetSrc(u.image_url)} alt={u.title} className="w-24 h-24 rounded-lg object-cover shrink-0 border border-slate-200" /> : null}
                  <div className="flex-1"><div className="font-display font-bold uppercase text-navy">{u.title}</div><p className="text-xs text-slate-500 mt-1">{u.description}</p><div className="font-num text-2xl font-extrabold text-brandred mt-2">{on ? "+ " : ""}{usd(u.price_cents)}</div>{on && <div className="text-[11px] font-bold uppercase text-successg">Added to total</div>}</div>
                </button>
                );
              })}
            </div>
          </div>
        )}

        {multi && <InvestmentSummary p={p} />}

        {/* Sign & Pay */}
        <div className="mb-card p-6" data-testid="sign-block">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <h3 className="font-display text-2xl font-bold uppercase text-navy">Accept & Sign</h3>
            <div className="text-right"><div className="text-xs text-slate-400 uppercase">{multi ? "Total Investment" : `Total · ${tLabel(tier)}`}</div><div className="font-num text-3xl font-extrabold text-navy" data-testid="sign-total">{usd(p.amount_cents)}</div>{p.upgrades_cents > 0 && <div className="text-xs text-slate-500" data-testid="sign-breakdown">Roof {usd(p.amount_cents - p.upgrades_cents)} + upgrades {usd(p.upgrades_cents)}</div>}<div className="text-xs text-slate-500">Deposit due today: <b className="text-brandred" data-testid="sign-deposit">{usd(p.deposit_cents)}</b></div></div>
          </div>

          {!signed ? (
            <>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Full legal name</label>
              <input data-testid="signer-name" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Type your name to sign"
                className="mt-1 mb-3 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy" />
              <SignaturePad onChange={setSig} />
              <button data-testid="sign-btn" onClick={sign} disabled={signing}
                className="w-full mt-4 bg-navy text-white font-bold py-3.5 rounded-xl border-2 border-navy flex items-center justify-center gap-2 active:translate-y-px">
                {signing ? <Loader2 className="w-5 h-5 animate-spin" /> : <PenLine className="w-5 h-5" />} Sign Contract
              </button>
              <p className="text-[11px] text-slate-400 mt-2 text-center">Legal-grade e-signature. An audit trail is recorded (viewed & signed timestamps).</p>
            </>
          ) : (
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-successg font-bold mb-3"><Check className="w-5 h-5" /> Signed by {p.signed_by}</div>
              {p.deposit_paid ? (
                <div className="bg-successg/10 text-successg font-bold rounded-xl py-3">Deposit received — your roof is scheduled! 🎉</div>
              ) : (
                <button data-testid="pay-deposit-btn" onClick={payDeposit} disabled={paying}
                  className="w-full bg-brandred text-white font-bold py-3.5 rounded-xl border-2 border-brandred flex items-center justify-center gap-2 active:translate-y-px">
                  {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} Pay Deposit {usd(p.deposit_cents)}
                </button>
              )}
            </div>
          )}
        </div>
        <div className="text-center text-xs text-slate-400 pb-6 space-y-0.5" data-testid="prop-brand-footer">
          <div className="font-semibold text-slate-500">{branding.company_name} · {branding.tagline}</div>
          {[branding.phone, branding.website, branding.address].filter(Boolean).length > 0 &&
            <div>{[branding.phone, branding.website, branding.address].filter(Boolean).join(" · ")}</div>}
          {branding.license && <div>License {branding.license}</div>}
        </div>
      </div>
    </div>
  );
}
